<template>
  <div
    class="message-widget"
    :class="{ focused }"
    @click="focusing"
    v-click-outside="deFocusing"
  >
    <transition-group
      class="chat-tabs"
      name="scale-bottom-right"
      v-if="activeConversations.length"
    >
      <ChatTab
        v-for="conversation in activeConversations"
        :key="conversation.id + `${typeof conversation.inSession}`"
        :conversation="conversation"
      />
    </transition-group>

    <ExpandedWidget />
    <GoProModal v-if="showGoProModal" />
    <div class="loading" v-if="loadingOpenChat">
      <lottie
        :options="{ animationData: LoadingAnimation }"
        :width="32"
        :height="32"
      />
    </div>
  </div>
</template>

<script>
import { fetchConversationById } from "@/api/messages"
import { mapActions, mapGetters, mapMutations } from "vuex"
import ExpandedWidget from "./ExpandedWidget"
import messageSoundFile from "@/assets/sound/message/instapro-message.wav"
import ClickOutside from "vue-click-outside"
import Lottie from "vue-lottie"
import LoadingAnimation from "@/assets/animations/ui/loading.json"

export default {
  data: function () {
    return {
      personalChatChannel: null,
      messageSound: null,
      focused: true,
      LoadingAnimation,
    }
  },
  components: {
    ExpandedWidget,
    Lottie,
    ChatTab: () => import("./ChatTab"),
    GoProModal: () => import("./GoProModal"),
  },
  computed: {
    ...mapGetters("Message", [
      "loadingOpenChat",
      "collapsed",
      "activeConversations",
      "chatUser",
      "conversations",
      "messageMap",
      "totalUnreadMessages",
      "showGoProModal",
    ]),
    ...mapGetters("Pusher", ["chatPusherClient"]),
  },
  methods: {
    ...mapActions("Pusher", ["initChatPusher"]),
    ...mapActions("User/Profile", ["loadSettings"]),
    ...mapActions("Message", [
      "loadChatUser",
      "openDirectConversation",
      "checkConversationInSession",
    ]),
    ...mapMutations("Message", [
      "setMessageMap",
      "setConversations",
      "setTotalUnreadMessages",
    ]),
    async subscribePersonalChatChannel () {
      await this.loadChatUser()
      if (!this.personalChatChannel) {
        this.personalChatChannel = this.chatPusherClient.subscribe(
          this.chatUser.broadcast_channel
        )
        this.personalChatChannel.bind("notify-message", this.handleNewMessage)
      }
    },
    async handleNewMessage (data) {
      const userId = data.message.user_id
      const conversationId = data.message.conversation_id
      const conversations = []
      let updatedConversation
      // Put message into correct conversation
      for (let conversation of this.conversations) {
        if (conversation.id === conversationId) {
          updatedConversation = conversation
          conversation.last_message = data.message
          if (userId !== this.chatUser.index) {
            conversation.unread_count++
            this.setTotalUnreadMessages(this.totalUnreadMessages + 1)
          }
          conversations.unshift(conversation)
        } else {
          conversations.push(conversation)
        }
      }
      const messageMap = this.messageMap
      const messages = this.messageMap[conversationId]

      if (userId !== this.chatUser.index) {
        this.messageSound.play().catch(e => {
          console.error(e)
        })
      }
      // If the text message is sent by current user
      if (userId === this.chatUser.index && data.message.type === "MESSAGE") {
        const newMessages = messages.map(message => {
          if (message.token === data.message.token) {
            message.sending = false
          }
          return message
        })
        messageMap[conversationId] = newMessages
        this.setMessageMap(messageMap)
      } else {
        // If the conversation is in list
        if (!updatedConversation) {
          const conversationData = await fetchConversationById(conversationId)
          const conversation = conversationData.result
          conversations.unshift(conversation)
        } else {
          if (messages) {
            messages.push(data.message)
            messageMap[conversationId] = messages
            this.setMessageMap(messageMap)
          }
        }
        await this.setConversations(conversations)
        if (
          data.message.type === "SESSION_START" ||
          data.message.type === "SESSION_END"
        ) {
          // check if there is a session started or end
          this.checkConversationInSession()
        }
      }
    },
    initializeAudio () {
      this.messageSound = new Audio()
      this.messageSound.src = messageSoundFile
      this.messageSound.volume = 0.5
    },
    listenChatOpen () {
      document.addEventListener("openChatEvent", e => {
        this.openDirectConversation({
          user: e.detail.user,
          forceOpen: e.detail.forceOpen,
        })
        setTimeout(() => {
          this.focusing()
        }, 100)
      })
    },
    focusing () {
      this.focused = true
    },
    deFocusing (e) {
      if (e.target.className === "message-coach__button") return
      this.focused = false
    },
  },
  mounted () {
    this.initChatPusher()
    this.subscribePersonalChatChannel()
    this.initializeAudio()
    this.listenChatOpen()
  },
  created () {
    this.loadSettings()
  },
  directives: {
    ClickOutside,
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/style/_mixins.scss";
.message-widget {
  position: relative;
  z-index: 9;
  &.focused {
    z-index: 20;
  }
  .chat-tabs {
    pointer-events: none;
    position: fixed;
    right: 60rem;
    bottom: 0;
    display: flex;
    z-index: 2;
    @include tablet-down {
      right: 0;
      height: calc(100% - 6.5rem);
    }
  }
  .loading {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    background: rgba($color: #fff, $alpha: 0.7);
  }
}

.scale-bottom-right-enter-active {
  transition: all 0.2s;
}

.scale-bottom-right-leave-active {
  transition: all 0.1s;
}
.scale-bottom-right-enter {
  transform: scale(0);
}
.scale-bottom-right-leave {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/message_widget/MessageWidget.vue